import { LOCATION_CHANGE } from '../../../../../shared/frontend/libs/router/redux-store';
import _ from 'lodash';
import {
  SET_CHARACTER_MYTHIC_PLUS_FILTERS,
  SET_CHARACTER_MYTHIC_PLUS_RUNS,
  SHOW_LOADING_SPINNER,
  TOGGLE_CHARACTER_MPLUS_SORT,
  TOGGLE_OPEN_DUNGEON
} from '../actions/characterActionCreator';
import { TAction } from '../../../../../shared/frontend/scripts/actions/types/action';
import { DUNGEON_SCORE_ALT_FACTOR, DUNGEON_SCORE_MAIN_FACTOR } from '../../../shared/enums';
import { Affixes } from '../../../../../shared/types/keystoneAffixTypes';

import { getBaseAffix } from '../../../../../shared/common/keystoneUtils';

const sortDungeons = (dungeons: any[], field: string, toggleCount: number): any[] => {
  const [unsortable = [], sortable = []] = _.reduce(
    dungeons,
    (result: any, curr: any) => {
      result[+(_.get(curr, field) !== undefined)].push(curr);
      return result;
    },
    [[], []]
  );

  const sortedDungeons = sortable
    .sort((instA: any, instB: any) => {
      const first = _.get(instA, field);
      const sec = _.get(instB, field);
      if (first === sec) {
        return toggleCount % 2 === 0
          ? _.get(instA, 'scoreSortKey') - _.get(instB, 'scoreSortKey')
          : _.get(instB, 'scoreSortKey') - _.get(instA, 'scoreSortKey');
      } else if (first > sec) {
        return toggleCount % 2 === 0 ? 1 : -1;
      }
      return toggleCount % 2 === 0 ? -1 : 1;
    })
    .concat(unsortable);

  return sortedDungeons;
};

const generateAffixKey = (modifiers: any[], numModifiersActive: number) => {
  const key = [];
  // SIMPLE ORDER
  key.push(_.map(modifiers, ({ name }) => name.toLowerCase()).sort());
  // SPECIAL ORDER
  key.push(_.map(modifiers, ({ name }) => name.toLowerCase()));

  return [numModifiersActive, ...key.reverse()].join('-');
};

export default function characterMythicPlusRuns(state: Partial<any> = {}, action: TAction<any>): Partial<any> {
  switch (action.type) {
    case SET_CHARACTER_MYTHIC_PLUS_RUNS: {
      const { payload } = action;
      const { sortField = 'scoreSortKey', toggleCount = 1 } = state;

      const { dungeons } = payload;

      const hasAlternateRun = dungeons.reduce((acc: boolean, value: any) => acc || !!value.alternate, false);

      const wkDung = dungeons.map((inst: any) => {
        const {
          weekly_modifiers = [],
          num_modifiers_active,
          mythic_level,
          clear_time_ms,
          alternate,
          score,
          dungeon: { keystone_timer_ms }
        } = inst;

        const modifiers = weekly_modifiers.length
          ? {
              weekly_modifiers: {
                sortKey: generateAffixKey(weekly_modifiers, num_modifiers_active),
                modifiers: weekly_modifiers
              }
            }
          : {};

        const levelSort = mythic_level
          ? {
              levelSortKey: mythic_level + 0.1 * (1.0 - clear_time_ms / keystone_timer_ms)
            }
          : {};

        const alternateLevelSort = alternate
          ? {
              alternateLevelSortKey: alternate.mythic_level + 0.1 * (1.0 - alternate.clear_time_ms / keystone_timer_ms)
            }
          : {};

        const scoreSort = {
          scoreSortKey: score || undefined
        };

        if (hasAlternateRun) {
          if (score) {
            scoreSort.scoreSortKey = score * DUNGEON_SCORE_MAIN_FACTOR;
          }

          if (alternate && alternate.score) {
            scoreSort.scoreSortKey += alternate.score * DUNGEON_SCORE_ALT_FACTOR;
          }
        }

        const affixSorts = {
          tyrannicalSortKey: undefined,
          fortifiedSortKey: undefined
        };
        const baseAffix = getBaseAffix(weekly_modifiers.map((m: any) => m.id));
        if (baseAffix === Affixes.TYRANNICAL) {
          affixSorts.tyrannicalSortKey = score || undefined;
          affixSorts.fortifiedSortKey = alternate?.score || undefined;
        } else {
          affixSorts.tyrannicalSortKey = alternate?.score || undefined;
          affixSorts.fortifiedSortKey = score || undefined;
        }

        return {
          ...inst,
          ...modifiers,
          ...levelSort,
          ...alternateLevelSort,
          ...scoreSort,
          ...affixSorts
        };
      });

      return {
        ...state,
        ...payload,
        ..._.omit(payload, ['dungeons']),
        sortField,
        toggleCount,
        dungeons: sortDungeons(wkDung, sortField, toggleCount),
        openDungeons: {}
      };
    }
    case SET_CHARACTER_MYTHIC_PLUS_FILTERS: {
      return {
        ...state,
        filters: {
          ...(state.filters || {
            mode: 'scored'
          }),
          ...action.payload
        },
        openDungeons: {}
      };
    }
    case SHOW_LOADING_SPINNER: {
      return {
        ...state,
        isLoading: action.payload
      };
    }
    case TOGGLE_OPEN_DUNGEON: {
      return {
        ...state,
        openDungeons: {
          ...(state.openDungeons || {}),
          [action.payload]: state.openDungeons ? !state.openDungeons[action.payload] : true
        }
      };
    }
    case TOGGLE_CHARACTER_MPLUS_SORT: {
      const { dungeons, sortField = null, toggleCount: count } = state;
      const {
        payload: { field, defaultSortOrder }
      } = action;
      const toggleCount = field === sortField ? count + 1 : defaultSortOrder === 'DESC' ? 1 : 2;

      return {
        ...state,
        sortField: field,
        toggleCount,
        dungeons: sortDungeons(dungeons, field, toggleCount)
      };
    }
    case LOCATION_CHANGE: {
      if (action.payload.isFirstRendering) {
        return state; // server render may have pre-populated state, so we don't want to nuke it
      }

      return state;
    }
    default: {
      return state;
    }
  }
}
